import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Checkbox } from 'ui-components/components/checkbox';
import { noop } from 'ui-components/default-props/noop';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Input, version2 } from 'ui-components/components/input';
import { Text, TextType } from 'ui-components/components/text';

import { messages } from './messages';
import { buildPhoneCaptureSchema, PHONE_FIELD } from './validation-schema';
import { useYupFormValidation } from '../../validation/use-yup-form-validation';

type PhoneNumberCaptureFormProps = {
  countryCode?: string;
  initialPhoneNumber?: string;
  isConsentChecked?: boolean;
  isConsentDisabled?: boolean;
  isDisabled?: boolean;
  shouldShowConsent?: boolean;
  shouldShowLabel?: boolean;
  onPhoneChange: (phoneNumber: string) => void;
  onConsentChange: (accepted: boolean) => void;
  onValidationChange: (isValid: boolean) => void;
};

export const PhoneNumberCaptureForm = ({
  countryCode,
  initialPhoneNumber = '',
  isConsentChecked = true,
  isConsentDisabled = false,
  isDisabled = false,
  shouldShowConsent = true,
  shouldShowLabel = true,
  onPhoneChange,
  onConsentChange,
  onValidationChange = noop,
}: PhoneNumberCaptureFormProps) => {
  const intl = useIntl();
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [hasBlurred, setHasBlurred] = useState(false);

  const schema = useMemo(() => buildPhoneCaptureSchema(countryCode), [countryCode]);
  const form = useMemo(() => ({ [PHONE_FIELD]: phoneNumber }), [phoneNumber]);

  const { errors, isValid } = useYupFormValidation({ form, schema });

  useEffect(() => {
    onValidationChange(isValid);
  }, [isValid, onValidationChange]);

  const phoneError = (errors as Record<string, { message?: string }>)?.[PHONE_FIELD];
  const errorMessage = hasBlurred && !isValid ? phoneError?.message : undefined;

  const handlePhoneChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget;
      setPhoneNumber(value);
      onPhoneChange(value);
    },
    [onPhoneChange],
  );

  const handleBlur = useCallback(() => {
    setHasBlurred(true);
  }, []);

  const handleConsentChange = useCallback(
    (checked: boolean) => {
      onConsentChange(checked);
    },
    [onConsentChange],
  );

  return (
    <FlexColumn gap={Gap.Space12}>
      {shouldShowLabel && <Text element='label' textType={TextType.Label} value={messages.phoneInputLabel} />}
      <Input
        disabled={isDisabled}
        inlineMessage={errorMessage}
        inlineMessageType={errorMessage ? 'error' : undefined}
        name='phone-capture'
        onBlur={handleBlur}
        onChange={handlePhoneChange}
        placeholder={intl.formatMessage(messages.phoneInputPlaceholder)}
        type='tel'
        value={phoneNumber}
        version={version2}
      />
      {shouldShowConsent && (
        <Checkbox
          checked={isConsentChecked}
          disabled={isConsentDisabled || isDisabled}
          label={intl.formatMessage(messages.consentCheckboxLabel)}
          onChange={handleConsentChange}
        />
      )}
    </FlexColumn>
  );
};
